import { useState } from 'react';
import { isActiveInvite, useCreateInvite, useInvites, useRemoveMember, useRevokeInvite } from '../../api/hooks';
import type { Invite, ListDetail, Member } from '../../api/types';
import { displayName, initials, inviteUrl } from '../../lib/next';
import { Button, IconButton } from '../../ui/Button';
import { IconCopy, IconSend, IconTrash, IconUserPlus, IconX } from '../../ui/icons';
import { Avatar, Badge, Skeleton } from '../../ui/Misc';
import { Sheet } from '../../ui/Sheet';
import { toast } from '../../ui/toast';

export async function copyText(text: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  // Fallback for insecure contexts (LAN IP over http).
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

function expiresLabel(inv: Invite) {
  if (!inv.expiresAt) return 'Never expires';
  const days = Math.ceil((new Date(inv.expiresAt).getTime() - Date.now()) / 86_400_000);
  if (days <= 1) return 'Expires within a day';
  return `Expires in ${days} days`;
}

export function ShareSheet({ list, open, onClose }: { list: ListDetail; open: boolean; onClose: () => void }) {
  const invites = useInvites(list.id, open);
  const create = useCreateInvite(list.id);
  const revoke = useRevokeInvite(list.id);
  const [fresh, setFresh] = useState<Invite | null>(null);

  const active = (invites.data ?? []).filter((i) => isActiveInvite(i));
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  async function onCopy(inv: Invite) {
    const ok = await copyText(inviteUrl(inv.token));
    if (ok) toast.success('Invite link copied');
    else toast.error('Couldn’t copy. Long-press the link to copy it.');
  }

  async function onNativeShare(inv: Invite) {
    try {
      await navigator.share({
        title: `Join “${list.title}” on Cady`,
        text: `Join my Cady list “${list.title}”`,
        url: inviteUrl(inv.token),
      });
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') void onCopy(inv);
    }
  }

  function close() {
    setFresh(null);
    onClose();
  }

  return (
    <Sheet
      open={open}
      onClose={close}
      title="Share list"
      description="Anyone with an invite link can join this list after signing in."
    >
      <section className="space-y-3">
        <h3 className="sr-only">
          Invite link
        </h3>
        {fresh ? (
          <div className="rounded-2xl border border-accent/40 bg-accent-soft/60 p-3">
            <label htmlFor="invite-link" className="mb-1.5 block text-sm font-semibold text-ink">
              Invite link
            </label>
            <input
              id="invite-link"
              readOnly
              value={inviteUrl(fresh.token)}
              onFocus={(e) => e.currentTarget.select()}
              className="block min-h-12 w-full rounded-xl border border-line bg-surface px-3 font-mono text-[16px] text-ink"
            />
            <div className="mt-3 flex gap-2">
              <Button variant="primary" className="flex-1" onClick={() => void onCopy(fresh)}>
                <IconCopy size={18} /> Copy link
              </Button>
              {canShare && (
                <Button variant="secondary" className="flex-1" onClick={() => void onNativeShare(fresh)}>
                  <IconSend size={18} /> Share…
                </Button>
              )}
            </div>
            <p className="mt-2 text-sm text-muted">{expiresLabel(fresh)}</p>
          </div>
        ) : (
          <Button
            variant="accent"
            size="lg"
            block
            loading={create.isPending}
            onClick={() => create.mutate(undefined, { onSuccess: (inv) => setFresh(inv) })}
          >
            <IconUserPlus size={20} /> Create invite link
          </Button>
        )}
      </section>

      <section aria-labelledby="links-h" className="mt-6">
        <h3 id="links-h" className="mb-2 text-sm font-semibold tracking-wide text-muted uppercase">
          Active links
        </h3>
        {invites.isPending ? (
          <Skeleton className="h-14" />
        ) : active.length === 0 ? (
          <p className="text-[0.95rem] text-muted">No active invite links.</p>
        ) : (
          <ul className="divide-y divide-line rounded-2xl border border-line">
            {active.map((inv) => (
              <li key={inv.id} className="flex items-center gap-1 py-1 pr-1 pl-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-mono text-sm text-ink">…{inv.token.slice(-8)}</p>
                  <p className="text-xs text-muted">{expiresLabel(inv)}</p>
                </div>
                <IconButton label="Copy invite link" onClick={() => void onCopy(inv)}>
                  <IconCopy size={19} />
                </IconButton>
                <IconButton
                  label="Revoke invite link"
                  className="text-danger"
                  onClick={() =>
                    revoke.mutate(inv.id, {
                      onSuccess: () => {
                        if (fresh?.id === inv.id) setFresh(null);
                        toast.info('Invite link revoked');
                      },
                    })
                  }
                >
                  <IconTrash size={19} />
                </IconButton>
              </li>
            ))}
          </ul>
        )}
      </section>

      <MembersSection list={list} />
    </Sheet>
  );
}

function MembersSection({ list }: { list: ListDetail }) {
  const remove = useRemoveMember(list.id);
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <section aria-labelledby="members-h" className="mt-6">
      <h3 id="members-h" className="mb-2 text-sm font-semibold tracking-wide text-muted uppercase">
        Members
      </h3>
      <ul className="divide-y divide-line rounded-2xl border border-line">
        <MemberRow member={list.owner} badge="Owner" />
        {list.members.map((m) => (
          <MemberRow key={m.id} member={m}>
            {confirming === m.id ? (
              <div className="flex items-center gap-1">
                <Button
                  variant="danger"
                  className="min-h-11 px-3"
                  loading={remove.isPending && remove.variables === m.id}
                  onClick={() =>
                    remove.mutate(m.id, {
                      onSuccess: () => toast.info(`${displayName(m)} was removed`),
                      onSettled: () => setConfirming(null),
                    })
                  }
                >
                  Remove
                </Button>
                <IconButton label="Cancel" onClick={() => setConfirming(null)}>
                  <IconX size={19} />
                </IconButton>
              </div>
            ) : (
              <IconButton
                label={`Remove ${displayName(m)}`}
                className="text-subtle hover:text-danger"
                onClick={() => setConfirming(m.id)}
              >
                <IconTrash size={19} />
              </IconButton>
            )}
          </MemberRow>
        ))}
      </ul>
      {list.members.length === 0 && (
        <p className="mt-2 text-[0.95rem] text-muted">No one else yet. Send an invite link to share this list.</p>
      )}
    </section>
  );
}

function MemberRow({ member, badge, children }: { member: Member; badge?: string; children?: React.ReactNode }) {
  return (
    <li className="flex min-h-16 items-center gap-3 py-2 pr-1 pl-3">
      <Avatar label={initials(member)} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink">
          {displayName(member)} {badge && <Badge>{badge}</Badge>}
        </p>
        {member.name && <p className="truncate text-sm text-muted">{member.email}</p>}
      </div>
      {children}
    </li>
  );
}
